/**
 * Product-page postcode delivery widget (inline — no modal).
 * ------------------------------------------------------------------
 * Renders a "Delivered to {State postcode}" header above a search box.
 * Typing digits filters the store's served postcodes in a dropdown; picking
 * one (or pressing Enter with an exact match) computes the Standard Shipping
 * fee locally and shows it (Free when the amount is 0).
 *
 * Data is compiled from the shipping-zone table into window.SHIPPING_RATES
 * (assets/shipping-rates-data.js). Two warehouses (Sydney / Melbourne), each
 * with two carriers (AUpost for light tiers, Allied for 22kg+ tiers). The
 * widget picks the LOWEST fee across the warehouses the variant has stock
 * in (pre-order variants default to Sydney only — see data-* attrs set by
 * snippets/shipping-calculator.liquid).
 *
 * The state prefix shown next to the postcode ("NSW 2220") is derived from
 * the Australian postcode -> state ranges (display only).
 */
(function () {
  if (window.ShippingCalculator) return;

  var STORAGE_KEY = 'shipping_calculator_postcode';
  var FREE_WEIGHT_KG = 22;
  var CONTACT_URL = 'https://www.livarahome.com.au/pages/contact-us';

  /* Australian postcode -> state abbreviation (display only). */
  function stateForPostcode(pc) {
    var n = parseInt(pc, 10);
    if (isNaN(n)) return '';
    if ((n >= 200 && n <= 299) || (n >= 2600 && n <= 2618) || (n >= 2900 && n <= 2920)) return 'ACT';
    if ((n >= 1000 && n <= 1999) || (n >= 2000 && n <= 2599) || (n >= 2619 && n <= 2899) || (n >= 2921 && n <= 2999)) return 'NSW';
    if ((n >= 3000 && n <= 3999) || (n >= 8000 && n <= 8999)) return 'VIC';
    if ((n >= 4000 && n <= 4999) || (n >= 9000 && n <= 9999)) return 'QLD';
    if (n >= 5000 && n <= 5799) return 'SA';
    if (n >= 6000 && n <= 6797) return 'WA';
    if (n >= 7000 && n <= 7999) return 'TAS';
    if (n >= 800 && n <= 899) return 'NT';
    return '';
  }

  function normalizePostcode(value) {
    var digits = String(value).replace(/[^0-9]/g, '');
    while (digits.length < 4) digits = '0' + digits;
    return digits.slice(0, 4);
  }

  function initWidget(root) {
    if (!root || root.dataset.shippingInitialized) return;
    root.dataset.shippingInitialized = 'true';

    var scope = root.closest('product-info') || root;
    var currency = root.dataset.currencySymbol || '$';
    var toggle = root.querySelector('[data-delivery-toggle]');
    var panel = root.querySelector('[data-delivery-panel]');
    var input = root.querySelector('[data-delivery-input]');
    var dropdown = root.querySelector('[data-delivery-dropdown]');
    var locationEl = root.querySelector('[data-delivery-location]');
    var resultEl = root.querySelector('[data-delivery-result]');
    var noteEl = root.querySelector('[data-delivery-note]');
    if (!input) return;

    var activePostcode = '';
    var servedPostcodes = [];

    /* ---------- helpers ---------- */

    function weightKg() {
      var v = parseFloat(root.dataset.weightKg);
      return isNaN(v) ? null : v;
    }

    function quantity() {
      var qtyEl = scope ? scope.querySelector('.quantity__input') : null;
      if (qtyEl) {
        var v = parseInt(qtyEl.value, 10);
        if (!isNaN(v) && v > 0) return v;
      }
      return 1;
    }

    function totalWeightKg() {
      var per = weightKg();
      if (per === null || per <= 0) return null;
      return per * quantity();
    }

    function formatPrice(price) {
      if (price === 0 || price === '0' || price === null || price === undefined) return 'Free';
      var rounded = Math.round(Number(price));
      var grouped = rounded.toLocaleString('en-US');
      return currency + grouped;
    }

    function isServed(pc) {
      var data = window.SHIPPING_RATES;
      if (!data) return false;
      var served = data.auPost || {};
      var allied = data.allied || {};
      function has(wh) {
        if (served[wh]) {
          for (var i = 0; i < served[wh].zones.length; i++) {
            if (served[wh].zones[i].postcodes.indexOf(pc) !== -1) return true;
          }
        }
        if (allied[wh]) {
          for (var j = 0; j < allied[wh].zones.length; j++) {
            if (allied[wh].zones[j].postcodes.indexOf(pc) !== -1) return true;
          }
        }
        return false;
      }
      return has('sydney') || has('melbourne');
    }

    function buildPostcodeList() {
      var data = window.SHIPPING_RATES;
      var seen = {};
      var out = [];
      function add(pc) {
        if (pc && !seen[pc]) { seen[pc] = 1; out.push(pc); }
      }
      if (data) {
        ['auPost', 'allied'].forEach(function (carrier) {
          var blk = data[carrier];
          if (!blk) return;
          ['sydney', 'melbourne'].forEach(function (wh) {
            var w = blk[wh];
            if (!w) return;
            w.zones.forEach(function (z) {
              z.postcodes.forEach(add);
            });
          });
        });
      }
      out.sort(function (a, b) {
        return parseInt(a, 10) - parseInt(b, 10);
      });
      return out;
    }

    function locationText(pc) {
      var st = stateForPostcode(pc);
      return st ? st + ' ' + pc : pc;
    }

    /* Rate lookups. A warehouse block = { brackets:[...], zones:[{postcodes,rates}] }.
       Find the ONE zone whose postcode list contains `pc` (region-restricted:
       for Allied the same postcode may sit in several zone columns — we take
       the first match, which is the correct owning region), then pick the
       rate for the weight bracket. `free` (0) passes through; a missing/None
       bracket value means that carrier has no price at this weight -> null.

       Weight split (per ops rule): AUpost covers 0-22kg, Allied covers
       22kg+. `rateForWeight` clamps the in-bracket selection to the weight
       tier, but only when `kg` is inside that carrier's band (AUpost kg<=22,
       Allied kg>22); outside the band the carrier is treated as not quoting
       so the other carrier can win. */
    function zoneForPostcode(block, pc) {
      if (!block || !block.zones) return null;
      for (var i = 0; i < block.zones.length; i++) {
        if (block.zones[i].postcodes.indexOf(pc) !== -1) return block.zones[i];
      }
      return null;
    }

    function rateForWeight(zones, kg, brackets, inBand) {
      var rates = zones.rates;
      if (!rates || !inBand) return null;
      // Brackets are upper bounds: b0..b_{n-2} bounded, last is null (open).
      // Pick the first bracket the weight fits under; if over the last bounded
      // bracket, use the final (open) tier.
      var idx = 0;
      for (var i = 0; i < brackets.length; i++) {
        var up = brackets[i];
        if (up === null) { idx = i; break; }
        if (kg <= up) { idx = i; break; }
        idx = i;
      }
      var price = rates[idx];
      if (price === null || price === undefined) return null;
      return price;
    }

    function carrierFee(carrier, wh, pc, kg) {
      var data = window.SHIPPING_RATES;
      var block = data && data[carrier] && data[carrier][wh];
      if (!block) return null;
      // AUpost covers 0-22kg only; Allied covers 22kg+ only. Anything outside
      // that carrier's band returns null so the other carrier can quote.
      var inBand = (carrier === 'auPost') ? kg <= 22 : kg > 22;
      var zone = zoneForPostcode(block, pc);
      if (!zone) return null;
      return rateForWeight(zone, kg, block.brackets, inBand);
    }

    // Lowest fee across both carriers for a warehouse, or null if neither
    // has a price at this weight / postcode.
    function feeForWarehouse(wh, pc, kg) {
      var a = carrierFee('auPost', wh, pc, kg);
      var b = carrierFee('allied', wh, pc, kg);
      var best = null;
      if (a !== null) best = a;
      if (b !== null && (best === null || b < best)) best = b;
      return best;
    }

    // Which warehouses can this variant ship from? Pre-order -> Sydney only.
    // In-stock -> whichever warehouses report stock (both if both do).
    function activeWarehouses() {
      if (root.getAttribute('data-preorder') === 'true') return ['sydney'];
      var whs = [];
      if (root.getAttribute('data-sydney-in-stock') === 'true') whs.push('sydney');
      if (root.getAttribute('data-melbourne-in-stock') === 'true') whs.push('melbourne');
      // No location reported stock but the item is purchasable: fall back to
      // the default Sydney warehouse.
      if (!whs.length) whs.push('sydney');
      return whs;
    }

    function feeFor(postcode, totalKg) {
      if (totalKg === null) return { error: 'no-weight' };
      var data = window.SHIPPING_RATES;
      if (!data) return { error: 'no-data' };

      var whs = activeWarehouses();
      var best = null;
      var bestWh = null;
      for (var i = 0; i < whs.length; i++) {
        var f = feeForWarehouse(whs[i], postcode, totalKg);
        if (f === null) continue;
        if (best === null || f < best) { best = f; bestWh = whs[i]; }
      }
      if (best === null) return { error: 'not-served' };
      return { price: best, warehouse: bestWh };
    }

    /* ---------- render helpers ---------- */

    // Success: "Standard Shipping   Free / $NN" in the theme-tinted box,
    // with a "leaves warehouse" delivery note and the originating warehouse
    // directly underneath.
    function showResult(price, warehouse) {
      if (noteEl) noteEl.hidden = true;
      if (!resultEl) return;
      resultEl.hidden = false;
      resultEl.classList.add('is-tinted');
      var whLabel = warehouse === 'melbourne' ? 'Melbourne' : 'Sydney';
      resultEl.innerHTML =
        '<div class="delivery-result__row">' +
          '<span class="delivery-result__label">Standard Shipping</span>' +
          '<span class="delivery-result__fee">' + formatPrice(price) + '</span>' +
        '</div>' +
        '<div class="delivery-result__ship-note">In-stock leaves warehouse in 2 business days</div>' +
        '<div class="delivery-result__warehouse">Ships from ' + whLabel + ' warehouse</div>';
    }

    // Unavailable location: same themed box, message only.
    function showNotServed() {
      if (!resultEl) return;
      if (noteEl) noteEl.hidden = true;
      resultEl.hidden = false;
      resultEl.classList.add('is-tinted');
      resultEl.innerHTML =
        '<div class="delivery-result__msg">' +
          'Shipping not available for this location. ' +
          'Contact <a href="' + CONTACT_URL + '">customer service</a> for more details.' +
        '</div>';
    }

    // Generic / data errors: red note text, no box.
    function showNote(html) {
      if (resultEl) {
        resultEl.hidden = true;
        resultEl.classList.remove('is-tinted');
      }
      if (noteEl) {
        noteEl.innerHTML = html;
        noteEl.hidden = false;
      }
    }

    function hideDropdown() {
      if (dropdown) {
        dropdown.hidden = true;
        dropdown.innerHTML = '';
      }
    }

    function renderDropdown(query) {
      var q = String(query || '').replace(/[^0-9]/g, '');
      if (!q) {
        hideDropdown();
        return;
      }
      var matches = servedPostcodes
        .filter(function (pc) {
          return pc.indexOf(q) === 0;
        })
        .slice(0, 12);
      if (!matches.length) {
        hideDropdown();
        return;
      }
      dropdown.innerHTML = matches
        .map(function (pc) {
          return (
            '<li class="delivery-dropdown__item" data-postcode="' + pc + '">' + locationText(pc) + '</li>'
          );
        })
        .join('');
      dropdown.hidden = false;
    }

    function applyPostcode(raw) {
      var pc = normalizePostcode(raw);
      activePostcode = pc;
      if (input) input.value = pc;
      if (locationEl) locationEl.textContent = locationText(pc);
      try {
        localStorage.setItem(STORAGE_KEY, pc);
      } catch (err) {}

      var fee = feeFor(pc, totalWeightKg());
      if (fee.error === 'no-data') {
        showNote('Delivery rates are not loaded yet. Please try again.');
      } else if (fee.error === 'no-weight') {
        showNote(
          'This item has no weight set, so delivery can’t be calculated. ' +
            'Please <a href="' + CONTACT_URL + '">contact us</a>.'
        );
      } else if (fee.error === 'not-served') {
        // Postcode not in the served table (or no carrier quotes for its
        // weight band): show the same kind of hint the postcode input gives
        // for a mistyped lookup — "check suburb spelling or postcode has 4
        // digits" — plus a contact link if the customer is confident the
        // postcode is right.
        showNote(
          'Check suburb spelling or postcode has 4 digits. ' +
          'If the postcode is correct, ' +
          '<a href="' + CONTACT_URL + '">contact us</a> to confirm delivery.'
        );
      } else {
        showResult(fee.price, fee.warehouse);
      }
      hideDropdown();
    }

    /* ---------- events ---------- */

    servedPostcodes = buildPostcodeList();

    if (input) {
      input.addEventListener('focus', function () {
        if (activePostcode && input.value === activePostcode) input.select();
      });

      input.addEventListener('input', function () {
        var digits = input.value.replace(/[^0-9]/g, '');
        if (input.value !== digits) input.value = digits;
        renderDropdown(input.value);
      });

      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          var digits = input.value.replace(/[^0-9]/g, '');
          if (digits) {
            var pc = normalizePostcode(digits);
            if (isServed(pc)) applyPostcode(pc);
            else renderDropdown(digits);
          }
        } else if (e.key === 'Escape') {
          hideDropdown();
        }
      });

      // Keep the dropdown clickable even when the input loses focus first.
      input.addEventListener('blur', function () {
        setTimeout(hideDropdown, 150);
      });
    }

    if (dropdown) {
      dropdown.addEventListener('click', function (e) {
        var item = e.target.closest('[data-postcode]');
        if (item) applyPostcode(item.getAttribute('data-postcode'));
      });
    }

    if (toggle) {
      toggle.addEventListener('click', function () {
        var isExpanded = toggle.getAttribute('aria-expanded') === 'true';
        toggle.setAttribute('aria-expanded', String(!isExpanded));
        toggle.classList.toggle('is-collapsed', isExpanded);
        if (!isExpanded && input) input.focus();
      });
    }

    // Recalculate if the shopper changes the quantity.
    var qtyEl = scope ? scope.querySelector('.quantity__input') : null;
    if (qtyEl) {
      qtyEl.addEventListener('change', function () {
        if (activePostcode) applyPostcode(activePostcode);
      });
    }

    // Restore the last checked postcode.
    try {
      var saved = localStorage.getItem(STORAGE_KEY);
      if (saved && isServed(normalizePostcode(saved))) applyPostcode(saved);
    } catch (err) {}
  }

  function initAll() {
    document.querySelectorAll('[data-shipping-calculator]').forEach(initWidget);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // The theme can re-render <product-info> (product swap / re-render).
  document.addEventListener('product-info:loaded', initAll);

  window.ShippingCalculator = { initAll: initAll };
})();